import { MailRowDataPropertyGetter } from 'owa-mail-list-store';
import { userDate, getTimestamp, addDays } from 'owa-datetime';
import type { TableView } from 'owa-mail-list-store';
import { isFeatureEnabled } from 'owa-feature-flags';

/**
 * Gets information about unread items with high CopilotInboxScore (4 or 5)
 * that were received in the last 24 hours or since the provided timestamp
 * @param tableView - The TableView to check
 * @param sinceTimestamp - Optional timestamp to use instead of 24 hours ago
 * @returns object containing item count and sender names
 */
export default function getPrioritizeRollupInformation(
    tableView: TableView,
    sinceTimestamp?: number | null
): {
    itemCount: number;
    senderNames: string;
} {
    if (!isFeatureEnabled('tri-priority-rollup') || !tableView?.rowKeys) {
        return { itemCount: 0, senderNames: '' };
    }

    const now = userDate();
    const cutoffTimestamp = sinceTimestamp ?? getTimestamp(addDays(now, -1));
    let qualifyingCount = 0;
    const senderSet = new Set<string>();

    for (const rowKey of tableView.rowKeys) {
        // Check if DateTimeReceived is after the cutoff timestamp
        const dateTimeReceived = MailRowDataPropertyGetter.getLastDeliveryOrRenewTimeStamp(
            rowKey,
            tableView
        );
        if (!dateTimeReceived) {
            continue;
        }

        const receivedTimestamp = getTimestamp(userDate(dateTimeReceived));
        if (receivedTimestamp <= cutoffTimestamp) {
            continue;
        }

        const isUnread = (MailRowDataPropertyGetter.getUnreadCount(rowKey, tableView) ?? 0) > 0;
        const isFlagged = MailRowDataPropertyGetter.getFlagStatus(rowKey, tableView) === 'Flagged';
        const isPinned = MailRowDataPropertyGetter.getIsPinned(rowKey, tableView);
        if (!isUnread || isFlagged || isPinned) {
            continue;
        }

        // Check if CopilotInboxScore is 4 or 5
        const copilotScore = MailRowDataPropertyGetter.getCopilotInboxScore(rowKey, tableView);
        if (copilotScore === 4 || copilotScore === 5) {
            qualifyingCount++;

            // Get sender information
            const senderMailbox = MailRowDataPropertyGetter.getLastSenderMailbox(rowKey, tableView);
            if (senderMailbox) {
                const senderName = senderMailbox.Name || senderMailbox.EmailAddress;
                if (senderName) {
                    senderSet.add(senderName);
                }
            }
        }
    }

    return { itemCount: qualifyingCount, senderNames: Array.from(senderSet).join(', ') };
}
